import { useEffect, useState } from "react";
import { IoMdReturnRight } from "react-icons/io";
import axios from "axios";

import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Navigation, Pagination } from "swiper/modules";
import { Icon } from "organisms/iconCommonent";

type Award = {
  cover: string;
  title: string;
  desc: string;
  year: string;
};

type AwardData = Record<string, Award>;

const AwardsSection = () => {
  const [awards, setAwards] = useState<AwardData>({});
  const [selected, setSelected] = useState<string>("");
  const [isLastSlide, setIsLastSlide] = useState(false);
  const [isFirstSlide, setIsFirstSlide] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchAwards = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_LOCAL_URL}/api/awards/show`
        );

        const data: AwardData = response.data.data.reduce(
          (acc: AwardData, award: Award) => {
            acc[award.title] = award;
            return acc;
          },
          {}
        );

        setAwards(data);
        if (Object.keys(data).length > 0) setSelected(Object.keys(data)[0]);
      } catch (error: any) {
        console.error("Error fetching awards:", error.message);
      }
    };

    fetchAwards();
  }, []);

  const handleSlideChange = (swiper: any) => {
    setIsFirstSlide(swiper.isBeginning);
    setIsLastSlide(swiper.isEnd);
  };

  const selectedAward = awards[selected];

  return (
    <section className="bg-[#FFFBEF]">
      {/* Desktop View */}
      <div className="w-container-xl blade-top-padding-lg blade-bottom-padding-lg hidden lg:block">
        <h1 className="text-[#07393C] font-lato-bold pb-1 border-b border-[#84848480]">
          Earned recognition for The Buddha Institute
        </h1>

        <div className="flex flex-col lg:flex-row gap-7 sm:gap-16 mt-10">
          {/* Left Section: Selected Award */}
          <div className="w-full lg:w-[40%] sticky top-2">
            <div className="bg-[#8DC63F] w-full min-h-[30rem] pb-12">
              {selectedAward?.cover && (
                <img
                  src={`${process.env.REACT_APP_LOCAL_URL}/${selectedAward.cover.replace(
                    /\\/g,
                    "/"
                  )}`}
                  alt={selected}
                  className="w-full object-cover object-center"
                  loading="lazy"
                />
              )}

              <div className="px-4 py-5">
                <h4 className="text-[#22574D] font-lato-bold py-2">
                  {selectedAward?.year}
                </h4>
                <h3 className="text-white font-lato-bold w-[90%]">
                  {selectedAward?.title}
                </h3>
                <h5 className={`font-lato-regular py-4 ${!expanded ? "line-clamp-3" : ""}`}>
                  {selectedAward?.desc}
                </h5>

                {selectedAward?.desc.split(" ").length > 30 && (
                  <button
                    className="mt-2 text-black hover:underline font-lato-regular"
                    onClick={() => setExpanded(!expanded)}
                  >
                    {expanded ? "Show less" : "Read more..."}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right Section: Tabs */}
          <div className="w-full lg:w-[60%]">
            <div className="pb-28 lg:border-l lg:border-[#84848480] lg:pt-24">
              {Object.keys(awards).map((title) => {
                const award = awards[title];
                const isSelected = selected === title;

                return (
                  <div
                    key={title}
                    className="cursor-pointer"
                    onClick={() => setSelected(title)}
                  >
                    <h3
                      className={`py-3 lg:px-4 text-36 font-lato-bold ${
                        isSelected ? "opacity-100 text-[#22574D]" : "opacity-30 text-[#22574D]"
                      }`}
                    >
                      {isSelected ? (
                        <div className="flex items-center gap-2">
                          <Icon icon={IoMdReturnRight} className="transition-transform duration-500 ease-in-out" />
                          {title}
                        </div>
                      ) : (
                        title
                      )}
                    </h3>
                    <h4 className="font-lato-bold text-[#8DC63F] pb-4 lg:px-4">{award.year}</h4>
                    <div className="lg:border-b-2 lg:border-[#84848480]"></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile View */}
      <div className="w-container-xl py-10 block lg:hidden">
        <h1 className="text-[#07393C] font-lato-bold pb-1 border-b border-[#84848480]">
          Earned recognition for The Buddha Institute
        </h1>

        <Swiper
          modules={[Navigation, Pagination]}
          navigation={{ prevEl: ".FirstprevBtn", nextEl: ".FirstnextBtn" }}
          pagination={{ el: ".swiper-progressbar-new", type: "progressbar" }}
          spaceBetween={16}
          grabCursor
          slidesPerView={1}
          onSlideChange={handleSlideChange}
          autoHeight
          breakpoints={{
            768: { slidesPerView: 2 },
            1024: { slidesPerView: 3 },
            1280: { slidesPerView: 3.5 },
            1536: { slidesPerView: 4.5 },
          }}
        >
          {Object.keys(awards).map((title) => {
            const award = awards[title];
            return (
              <SwiperSlide key={title} className="!min-h-[30rem]">
                <div className="bg-[#8DC63F] p-4 h-full">
                  {award.cover && (
                    <img
                      src={`${process.env.REACT_APP_LOCAL_URL}/${award.cover.replace(/\\/g, "/")}`}
                      alt={title}
                      className="object-contain w-full h-full"
                      loading="lazy"
                    />
                  )}
                  <div className="py-5">
                    <h3 className="text-36 text-white font-lato-bold">{award.title}</h3>
                    <h4 className="text-[#22574D] font-lato-bold py-2">{award.year}</h4>
                    <h5 className="font-lato-regular py-4">{award.desc}</h5>
                  </div>
                </div>
              </SwiperSlide>
            );
          })}

          <div className="relative flex justify-end">
           
            <div className="custom-navigation mt-3 flex gap-2">
              <button className={`FirstprevBtn cursor-pointer w-10 ${isFirstSlide ? "opacity-40" : ""}`} aria-label="Previous">
                <img src="/BudhaFellowProgram/BFP_PreviousBtn.png" alt="Previous" className="w-full h-full object-contain" />
              </button>
              <button className={`FirstnextBtn cursor-pointer w-10 ${isLastSlide ? "opacity-40" : ""}`} aria-label="Next">
                <img src="/BudhaFellowProgram/BFP_NextBtn.png" alt="Next" className="w-full h-full object-contain" />
              </button>
            </div>
          </div>
        </Swiper>
      </div>
    </section>
  );
};

export default AwardsSection;
